import React, { useEffect, useState } from 'react';
import type {
  SelectChangeEvent} from '@mui/material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import WindowIcon from '@mui/icons-material/Window';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import type { UserRole } from '../types';

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';
const AZURE_CLIENT_ID = import.meta.env.VITE_AZURE_AD_CLIENT_ID ?? '';
const AZURE_TENANT_ID = import.meta.env.VITE_AZURE_AD_TENANT_ID ?? '';

const buildAzureAuthUrl = (): string => {
  const params = new URLSearchParams({
    client_id: AZURE_CLIENT_ID,
    response_type: 'code',
    redirect_uri: `${window.location.origin}/login`,
    scope: 'openid profile email User.Read',
    response_mode: 'query',
  });
  return `https://login.microsoftonline.com/${AZURE_TENANT_ID}/oauth2/v2.0/authorize?${params.toString()}`;
};

const FEATURES = [
  'AI-powered lesson creation',
  'Multi-phase workflow',
  'Team collaboration',
];

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [bypassEmail, setBypassEmail] = useState('');
  const [bypassRole, setBypassRole] = useState<UserRole>('author');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Handle Azure AD redirect with auth code
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      handleAzureCallback(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAzureCallback = async (code: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authService.loginWithAzureAD(code);
      login(response.token, response.user);
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Azure AD authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAzureLogin = () => {
    if (!AZURE_CLIENT_ID || !AZURE_TENANT_ID) {
      setError('Azure AD is not configured. Please use Dev Login.');
      return;
    }
    window.location.href = buildAzureAuthUrl();
  };

  const handleBypassLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bypassEmail.trim()) {
      setError('Email is required.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await authService.bypassLogin(bypassEmail, bypassRole);
      login(response.token, response.user);
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Dev login failed. Make sure the backend is running.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Login form content (shared between mobile and desktop right panel) ──
  const LoginForm = (
    <Box
      sx={{
        width: '100%',
        maxWidth: 400,
        mx: 'auto',
        px: { xs: 3, sm: 4 },
        py: { xs: 4, md: 0 },
      }}
    >
      {/* Icon + headings */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={700} color="text.primary">
          Welcome back
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Sign in to continue to LessonCraft AI
      </Typography>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading spinner */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Azure AD button */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        size="large"
        startIcon={<WindowIcon />}
        onClick={handleAzureLogin}
        disabled={isLoading}
        sx={{
          py: 1.5,
          borderRadius: 2,
          fontWeight: 700,
          fontSize: '0.9rem',
          mb: 2,
        }}
      >
        Continue with Azure AD
      </Button>

      {/* Dev bypass section */}
      {BYPASS_AUTH && (
        <>
          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary">
              DEV BYPASS
            </Typography>
          </Divider>

          <Box
            component="form"
            onSubmit={handleBypassLogin}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              label="Email"
              type="email"
              size="small"
              fullWidth
              value={bypassEmail}
              onChange={(e) => setBypassEmail(e.target.value)}
              disabled={isLoading}
              placeholder="dev@example.com"
            />

            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={bypassRole}
                onChange={(e: SelectChangeEvent) =>
                  setBypassRole(e.target.value as UserRole)
                }
                disabled={isLoading}
              >
                <MenuItem value="author">Author</MenuItem>
                <MenuItem value="content_manager">Content Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <Button
              type="submit"
              variant="outlined"
              fullWidth
              size="small"
              disabled={isLoading}
              sx={{ borderRadius: 2 }}
            >
              Dev Login
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  // ── Mobile layout ──
  if (isMobile) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        {/* Gradient banner at top for mobile */}
        <Box
          sx={{
            background: 'linear-gradient(160deg, #0C2541 0%, #003359 50%, #136DD3 100%)',
            py: 3,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <SchoolIcon sx={{ color: '#fff', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
              LessonCraft AI
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              English Lesson Slide Generator
            </Typography>
          </Box>
        </Box>

        {/* Login form */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
          }}
        >
          {LoginForm}
        </Box>
      </Box>
    );
  }

  // ── Desktop split-screen layout ──
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left panel — gradient branding */}
      <Box
        sx={{
          width: '45%',
          flexShrink: 0,
          background: 'linear-gradient(160deg, #0C2541 0%, #003359 50%, #136DD3 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
        }}
      >
        {/* Top: app name + subtitle */}
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', mb: 1 }}>
            LessonCraft AI
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 5 }}>
            English Lesson Slide Generator
          </Typography>

          {/* Feature bullets */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {FEATURES.map((feature) => (
              <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CheckCircleOutlineIcon sx={{ color: '#fff', fontSize: 22 }} />
                <Typography variant="body1" sx={{ color: '#fff' }}>
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom: WSE caption */}
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          Wall Street English
        </Typography>
      </Box>

      {/* Right panel — login form */}
      <Box
        sx={{
          flexGrow: 1,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {LoginForm}
      </Box>
    </Box>
  );
};
